// Cart for signed-out visitors.
//
// The server cart is bound to a user (Cart.user) and every /api/cart route sits
// behind authenticate, so a guest has nowhere to put items. This keeps a minimal
// cart in localStorage and hydrates it against the live product API, so prices,
// stock and titles are always current — only { productId, size, quantity } is
// stored, never a price snapshot that could go stale.
//
// On sign-in, mergeGuestCart() (Redux/Customers/Cart/Action.js) pushes these
// lines into the user's server cart and clears this store.

const KEY = "guest_cart";

// A synthetic cart-item id. CartItem passes item._id straight to
// update/removeCartItem, so guest lines need an id those actions can recognise
// and route locally instead of to the API.
export const guestItemId = (productId, size) => `guest:${productId}:${size || ""}`;
export const isGuestItemId = (id) => String(id || "").startsWith("guest:");

export function readGuestCart() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((l) => l && l.productId)
      .map((l) => ({
        productId: String(l.productId),
        size: l.size || "",
        quantity: Math.max(1, Number(l.quantity) || 1),
      }));
  } catch (e) {
    return []; // malformed/unavailable storage — behave as an empty cart
  }
}

function write(lines) {
  try {
    localStorage.setItem(KEY, JSON.stringify(lines));
  } catch (e) {
    /* storage full or blocked (private mode) — the cart just won't persist */
  }
}

// Same product AND same size is one line, matching the server's uniqueness rule
// (CartItem is keyed on cart + product + userId + size).
export function addGuestItem({ productId, size, quantity = 1 }) {
  const lines = readGuestCart();
  const qty = Math.max(1, Number(quantity) || 1);
  const existing = lines.find(
    (l) => l.productId === String(productId) && (l.size || "") === (size || "")
  );
  if (existing) existing.quantity += qty;
  else lines.push({ productId: String(productId), size: size || "", quantity: qty });
  write(lines);
  return lines;
}

export function setGuestItemQuantity(itemId, quantity) {
  const lines = readGuestCart().map((l) =>
    guestItemId(l.productId, l.size) === itemId
      ? { ...l, quantity: Math.max(1, Number(quantity) || 1) }
      : l
  );
  write(lines);
  return lines;
}

export function removeGuestItem(itemId) {
  const lines = readGuestCart().filter(
    (l) => guestItemId(l.productId, l.size) !== itemId
  );
  write(lines);
  return lines;
}

export function clearGuestCart() {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
}

export function guestCartCount() {
  return readGuestCart().reduce((n, l) => n + l.quantity, 0);
}
